import React, { useState } from 'react';
import styles from '../BranchDrawer.module.css';
import Ico from '../../common/Ico';
import Pill from '../../common/Pill';
import { statusLabel, statusTokens, STATUS_KINDS } from '../statusConfig';
import { getSourceComponentMeta } from '../sourceComponents';
import {
  ICO_CHEVRIGHT,
  ICO_EXTERNAL,
  EXTERNAL_NIGHTLY_OPERATION_KEYS,
  NIGHTLY_STEP_CONFIG,
  NIGHTLY_STEP_ORDER,
  NON_VALIDATION_STEP_KEYS,
} from './constants';
import { commitsFromRun, displayStatusForNightlyOperation, getStepIcon, isNightlyRequiredStep } from './helpers';
import { AutOutcomeInline } from './OutcomeSummary';
import RolloutCard from './RolloutCard';
import FreeTierRolloutCard from './FreeTierRolloutCard';

function CommitCards({ commits }) {
  if (!commits.length) return null;
  return (
    <div className={styles.sectionGroup}>
      <div className={styles.overline}>Source commits</div>
      <div className={styles.commitGrid}>
        {commits.map(({ id, sha }) => {
          const meta = getSourceComponentMeta(id);
          if (!meta) return null;
          return (
            <a
              key={id}
              href={`${meta.repo}/commit/${sha}`}
              target="_blank"
              rel="noreferrer"
              className={styles.commitCard}
              onClick={e => e.stopPropagation()}
            >
              <span className={styles.commitCardTile} style={{ background: meta.bg }}>
                {meta.icon(13)}
              </span>
              <span className={styles.commitCardText}>
                <span className={styles.commitCardLabel}>{meta.label}</span>
                <code className={styles.commitCardSha}>{sha.slice(0, 7)}</code>
              </span>
              <Ico d={ICO_EXTERNAL} size={12} style={{ color: 'var(--fg-link)', flexShrink: 0 }} />
            </a>
          );
        })}
      </div>
    </div>
  );
}

function stepsFromSource(source) {
  return Object.entries(source || {})
    .map(([key, val]) => {
      const cfg = NIGHTLY_STEP_CONFIG[key];
      return {
        key,
        name: cfg?.label || key,
        meta: cfg?.name || key,
        stepName: cfg?.name || key,
        ...val,
      };
    })
    .sort((a, b) => {
      const ia = NIGHTLY_STEP_ORDER.indexOf(a.key);
      const ib = NIGHTLY_STEP_ORDER.indexOf(b.key);
      return (ia === -1 ? Infinity : ia) - (ib === -1 ? Infinity : ib);
    });
}

export function operationsForEntry(kind, data) {
  return kind === 'run'
    ? stepsFromSource(data.result).filter((op) => !NON_VALIDATION_STEP_KEYS.includes(op.key))
    : [];
}

export function changelogOperationsForEntry(kind, data) {
  return kind === 'run'
    ? stepsFromSource(data.result).filter((op) => NON_VALIDATION_STEP_KEYS.includes(op.key))
    : [];
}

export function deployOperationsForEntry(kind, data) {
  return kind === 'run' ? stepsFromSource(data.deploy_result) : [];
}

function OpRow({ op, onOpenOp }) {
  const displayStatus = displayStatusForNightlyOperation(op);
  const tok = statusTokens(displayStatus) || STATUS_KINDS.neutral;
  const isExternal = !!op.op_url && (
    !op.operation_id || EXTERNAL_NIGHTLY_OPERATION_KEYS.includes(op.key)
  );
  return (
    <button
      className={styles.opRow}
      onClick={() => isExternal
        ? window.open(op.op_url, '_blank', 'noreferrer')
        : onOpenOp?.({ name: op.name, op })
      }
      type="button"
    >
      <span className={styles.opIconTile} style={{ background: tok.bg, color: tok.fg }}>
        <Ico d={getStepIcon(op.key || op.name)} size={13} strokeWidth={1.7} />
      </span>
      <span className={styles.opInfo}>
        <span className={styles.opName}>
          {op.name}
          {isNightlyRequiredStep(op.key) && (
            <span className={styles.opRequired} title="Required: decides the run's success and auto-rollout">*</span>
          )}
        </span>
        {op.meta && <span className={styles.opMeta}>{op.meta}</span>}
      </span>
      {op.outcome && <AutOutcomeInline outcome={op.outcome} />}
      <Pill status={displayStatus} dot={false}>{statusLabel(displayStatus)}</Pill>
      {isExternal
        ? <Ico d={ICO_EXTERNAL} size={13} style={{ color: 'var(--blue-600)', flexShrink: 0 }} />
        : <Ico d={ICO_CHEVRIGHT} size={14} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
      }
    </button>
  );
}

export default function RunOpsView({ entry, onOpenOp, clusterMap }) {
  const { kind, data } = entry;
  const [clusterQ, setClusterQ] = useState('');

  const ops = operationsForEntry(kind, data);
  const deployOps = deployOperationsForEntry(kind, data);
  const commits = kind === 'run' ? commitsFromRun(data) : [];

  return (
    <div className={styles.section}>
      <CommitCards commits={commits} />
      <div className={styles.sectionGroup}>
        <div className={styles.overline}>Operations</div>
        <div className={styles.opList}>
          {ops.map((op) => <OpRow key={op.name} op={op} onOpenOp={onOpenOp} />)}
          {ops.length === 0 && <div className={styles.empty}>No operations recorded yet.</div>}
        </div>
      </div>

      {deployOps.length > 0 && (
        <div className={styles.sectionGroup}>
          <div className={styles.overline}>Deployment</div>
          <div className={styles.opList}>
            {deployOps.map((op) => <OpRow key={op.name} op={op} onOpenOp={onOpenOp} />)}
          </div>
        </div>
      )}

      {kind === 'run' && (
        <RolloutCard
          build={data}
          clusterMap={clusterMap}
          clusterQ={clusterQ}
          onClusterQChange={setClusterQ}
        />
      )}

      {kind === 'run' && <FreeTierRolloutCard build={data} />}
    </div>
  );
}
